import sortBy from 'lodash/sortBy';

import type { ComboboxItemGroup } from '@mantine/core';
import type { RivalType } from 'api/api.types';

export type BuildPrioritizedRivalsOptionsParams = {
  rivals: RivalType[];
  prioritizedSlugs: string[];
  otherCompetitorsLabel?: string;
};

/**
 * Organizes rivals into prioritized and other sections for Mantine Select.
 *
 * @param params - Configuration for building rival options
 * @returns Array of ComboboxItem or ComboboxItemGroup for Mantine Select `data` prop
 *
 * Edge cases:
 * - If no prioritized rivals found: Returns all rivals alphabetically, no groups
 * - If all rivals are prioritized: Returns all rivals alphabetically, no groups
 * - If both exist: Returns grouped data with prioritized first, then other competitors
 */
export function buildPrioritizedRivalsOptions({
  rivals,
  prioritizedSlugs,
  otherCompetitorsLabel = 'Other Competitors',
}: BuildPrioritizedRivalsOptionsParams): (
  | { value: string; label: string }
  | ComboboxItemGroup
)[] {
  // Partition rivals into prioritized and other groups
  const prioritizedRivals: RivalType[] = [];
  const otherRivals: RivalType[] = [];

  rivals.forEach((rival) => {
    if (prioritizedSlugs.includes(rival.slug)) {
      prioritizedRivals.push(rival);
    } else {
      otherRivals.push(rival);
    }
  });

  const sortAndMap = (
    rivalsList: RivalType[],
  ): { value: string; label: string }[] => {
    return sortBy(rivalsList, [(rival) => rival.name.toLowerCase()]).map(
      (rival) => ({
        value: rival.id.toString(),
        label: rival.name,
      }),
    );
  };

  const prioritizedOptions = sortAndMap(prioritizedRivals);
  const otherOptions = sortAndMap(otherRivals);

  // Handle edge cases: no prioritization needed
  if (prioritizedOptions.length === 0) {
    return otherOptions;
  }

  if (otherOptions.length === 0) {
    return prioritizedOptions;
  }

  // Return grouped data for Mantine Select
  return [
    ...prioritizedOptions,
    { group: otherCompetitorsLabel, items: otherOptions },
  ];
}
